import classNames from "classnames";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ToastContent from "~@/components/layout/ToastContent";
import { RequiredModalProps } from "~@/components/modals/PasswordChangeModal";
import {
  MakeTariffMethod,
  useGetOrganizationsQuery,
  useMakeTariffMutation,
} from "~@/graphql/codegen/generated";
import { XMark17x17Icon } from "~@/icons";
import { rootElement } from "~@/index";

const CancellationModal: FC<RequiredModalProps> = ({
  isModalOpen,
  closeModal,
}) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { data: organizationsData } = useGetOrganizationsQuery();

  const currentOrganization = useMemo(() => {
    return organizationsData?.getOrganizations?.current;
  }, [organizationsData]);

  const [makeTariff, { loading: isTariffUpdating }] = useMakeTariffMutation({
    onError() {
      toast(
        <ToastContent
          type="error"
          message={t("graphqlError.update-organization")}
        />
      );
    },
  });

  const cancelSubscription = useCallback(() => {
    if (isTariffUpdating || !currentOrganization) return;

    return makeTariff({
      update(cache) {
        cache.modify({
          id: cache.identify(currentOrganization),
          fields: {
            isCanceled() {
              return true;
            },
          },
        });
      },
      onCompleted() {
        closeModal();

        navigate("..");
      },
      variables: {
        method: MakeTariffMethod.Cancel,
        tariffId: null,
      },
    });
  }, [isTariffUpdating, currentOrganization, makeTariff, closeModal, navigate]);

  return (
    <Modal
      isOpen={isModalOpen}
      onRequestClose={closeModal}
      className="modal unsubscribe-modal"
      overlayClassName="overlay overlay--has-transparency-30"
      contentLabel="unsubscribe modal"
      style={{
        content: {
          width: 480,
        },
      }}
      parentSelector={() => rootElement}
      closeTimeoutMS={200}
    >
      <button
        className="modal__close-button"
        type="button"
        onClick={closeModal}
      >
        <span className="icon">
          <XMark17x17Icon />
        </span>
      </button>
      <div className="modal__header unsubscribe-modal__header">
        <h2 className="unsubscribe-modal__title">
          {t("action.cancel-account")}
        </h2>
      </div>
      <div className="modal__body unsubscribe-modal__body">
        <div className="block block--has-mb-6">
          <h3 className="unsubscribe-modal__subtitle">
            {t("packages.cancelling-account-reason")}
          </h3>
        </div>
      </div>
      <div className="modal__footer unsubscribe-modal__footer">
        <div className="buttons buttons--has-gap-5">
          <button
            className="button button--h-48 button--stroke-gray-2"
            type="button"
            onClick={closeModal}
          >
            <span>{t("action.go-back")}</span>
          </button>
          <button
            className={classNames("button button--h-48 button--fill-black-0", {
              "button--is-loading": isTariffUpdating,
            })}
            type="button"
            onClick={cancelSubscription}
          >
            <span>{t("action.submit-cancelling")}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CancellationModal;
